import { useCallback } from 'react';
import { RelatedType_Enum } from '@weave/schema-gen-ts/dist/schemas/email/shared/v1/enums.pb';
import { MessageType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { useQueryClient } from 'react-query';
import { DigitalFormsQueries as queryEndpointKeys } from '@frontend/api-digital-forms';
import { useSendMutation } from '@frontend/api-email-send';
import { useTranslation } from '@frontend/i18n';
import { useAlert } from '@frontend/design-system';
import { useSendFormsContext } from '../providers/context';
import { useSendFormsStore } from '../providers/store';
import { convertToHTML } from '../utils/email.utils';
import { finishSendFormTracking } from '../utils/tracker/tracker.utils';

type SendDocumentFn = () => Promise<void>;

export interface useSendDocumentResult {
  sendDocument: SendDocumentFn;
}

export const useSendDocument = (): useSendDocumentResult => {
  const { t } = useTranslation('digital-forms-sending-experience');
  const alert = useAlert();
  const { sendFormsModalControls, origin } = useSendFormsContext();
  const { firstName, locationId, submissionToken, clearStore, getEmailPayload } = useSendFormsStore([
    'firstName',
    'locationId',
    'submissionToken',
    'clearStore',
    'getEmailPayload',
  ]);
  const queryClient = useQueryClient();
  const { mutateAsync: sendEmail } = useSendMutation();
  const { closeModal: closeSendFormsModal } = sendFormsModalControls;

  const sendDocument = useCallback<SendDocumentFn>(async () => {
    const { email, emailBody, emailSubject } = getEmailPayload();
    if (email.trim() === '') {
      return;
    }

    try {
      await sendEmail({
        locationId,
        email: {
          body: {
            rawHtml: convertToHTML(emailBody),
          },
          subject: emailSubject,
          emailType: MessageType_Enum.MESSAGING_MANUAL_FORM,
          relatedIds: [
            {
              type: RelatedType_Enum.RELATED_TYPE_FORM,
              id: submissionToken,
            },
          ],
          to: [
            {
              email: email,
              name: firstName,
            },
          ],
        },
      });

      finishSendFormTracking({
        isComplete: true,
        origin,
        deliveryMethod: 'email',
      });
      alert.success(t('Forms sent to {{firstName}} successfully', { firstName }));
      closeSendFormsModal();
      queryClient.invalidateQueries(queryEndpointKeys.endpointKeys.submissionDetailsForPerson);
      clearStore(origin);
    } catch (error) {
      alert.error(t('Failed to send email'));
    }
  }, [
    sendEmail,
    getEmailPayload,
    locationId,
    submissionToken,
    closeSendFormsModal,
    alert,
    t,
    firstName,
    clearStore,
    queryClient,
    origin,
  ]);

  return { sendDocument };
};
